<template>
  <div>
    <div v-if="count > 0">
      <el-table
        stripe
        :data="timesheets"
        style="width: 100%"
        :border="true"
        class="depth">

        <el-table-column
          label="Status"
          align="center"
          width="110">
          <template slot-scope="scope">
            <el-tag size="small" :type="setType(scope.row.status)" effect="dark">{{ setValue(scope.row.status) }}</el-tag>      
          </template>
        </el-table-column>

        <el-table-column
          label="Week #"
          align="center"
          width="75">
          <template slot-scope="scope">
            {{ scope.row.week }}
          </template>
        </el-table-column>

        <el-table-column
          label="Year"
          align="center"
          width="75">
          <template slot-scope="scope">
            {{ scope.row.year }}
          </template>
        </el-table-column>

        <el-table-column
          label="Name">
          <template slot-scope="scope">
            {{ scope.row.user.profile.first_name }}
            {{ scope.row.user.profile.last_name }}
          </template>
        </el-table-column>

        <el-table-column
          label="Submitted"
          align="right">
          <template slot-scope="scope">
            {{ scope.row.created_at | formatTime('MMMM DD, YYYY hh:mm a') }}
          </template>
        </el-table-column>

        <el-table-column
          label=""
          width="120" align="center">
          <template slot-scope="scope">
            <router-link  :to="{ name: 'timesheet', params: { id: scope.row.id } }"><el-button type="primary" size="mini"><i class="fas fa-search"></i></el-button></router-link>
          </template>
        </el-table-column>

      </el-table>

      <div class="pagination">
        <el-pagination
          @current-change="handleCurrentChange"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          layout="total, prev, pager, next"
          :total="total">
        </el-pagination>
      </div>
    </div>

    <div v-else>
      <el-alert type="info" :closable="false" title="There are no timesheets entries!" center></el-alert>
    </div>
  </div>
</template>

<script>
  import { map, isEmpty, forEach, includes } from 'lodash';
  import { mapActions, mapGetters } from 'vuex';

  import { LOG_ACTIONS } from '@/store/modules/log/constants';
  import { formatTime } from '@/filters/time';

  export default {
    name: 'TimesheetsList',
    props: {
      timesheets: {
        type: Array,
        required: false,
      },
    },
    data() {
      return {
        loading: true,
        reloading: false,
        loadError: null,
        currentPage: 1,
        pageSize: 1,
        total: 0,
        sortColumn: 'created_at',
        sortOrder: 'desc',
        filters: {},
      };
    },
    computed: {
      ...mapGetters('entities/timesheets', ['query']),
      count() {
        return this.timesheets ? this.timesheets.length : 0;
      },
      authorFilters() {
        const users = this.$store.getters['entities/users/query']()
          .with('profile')
          .where('admin', false)
          .get();

        if (!this.userId) {
          return map(users, (user) => {
            const text = (user.profile) ? `${user.email} (${user.profile.first_name} ${user.profile.last_name})` : user.email;

            return {
              text,
              value: user.id,
            };
          });
        }

        return [];
      },

      actionFilters() {
        return map(LOG_ACTIONS, index => ({
          text: this.$t(`modules.log.actions.${index}`),
          value: index,
        }));
      },

      logs() {
        const baseQuery = this.query()
                .with('author.profile')
                .orderBy(this.sortColumn, this.sortOrder)
                .offset((this.currentPage - 1) * this.pageSize)
                .limit(this.pageSize);

        forEach(this.filters, (filterOptions, field) => {
          if (!isEmpty(filterOptions)) {
            baseQuery.where(field.replace('_filter', ''), value => includes(filterOptions, parseInt(value, 10)));
          }
        });

        if (this.userId) {
          baseQuery.where('author_id', this.userId);
        }

        return baseQuery.get();
      },
    },
    methods: {
      ...mapActions('entities/timesheets', ['fetchTimesheets']),
      ...mapActions('entities/users', ['fetchUsers']),

      updating(timesheet) {
        return this.$store.getters['entities/timesheets/isUpdating'](timesheet.id);
      },

      buildParams() {
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
          sort_field: this.sortColumn,
          sort_order: this.sortOrder,
          ...this.filters,
        };

        if (this.userId) params.user_id = this.userId;

        return params;
      },

      fetchData() {
        this.showLoader();
        this.loadError = null;
        this.fetchLogs(this.buildParams())
          .then((response) => {
            if (response.status) {
              this.total = response.data.meta.total;
            } else {
              this.loadError = response.error;
            }

            this.hideLoader();
          });
      },

      actionFormatter(row, column) {
        const property = column.property;
        return this.$t(`modules.log.actions.${row[property]}`);
      },

      dateFormatter(row, column) {
        const property = column.property;
        return formatTime(row[property], 'MMMM DD, YYYY hh:mm a');
      },

      handleCurrentChange(val) {
        this.currentPage = val;
        this.fetchData();
      },

      handleSortChange({ prop, order }) {
        this.sortColumn = prop;
        this.sortOrder = order === 'descending' ? 'desc' : 'asc';
        this.fetchData();
      },

      handleFilterChange(filters) {
        this.filters = filters;
        this.fetchData();
      },

      showLoader() {
        if (this.logs.length) {
          this.reloading = true;
        } else {
          this.loading = true;
        }
      },

      hideLoader() {
        this.reloading = false;
        this.loading = false;
      },

      setType(status) {
        switch (status) {
          case 'approved':
            return 'success';
          case 'rejected':
            return 'danger';
          case 'pending':
            return 'warning';
          default:
            return 'info';
        }
      },
      setValue(status) {
        switch (status) {
          case 'approved':
            return 'Approved';
          case 'rejected':
            return 'Rejected';
          case 'pending':
            return 'Pending';
          default:
            return 'Not Submitted';
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .not-found{
    text-align: center;
    padding: 40px 0;
  }
  .pagination {
    margin-top: 20px;
    text-align: center;
  }
</style>


// WEBPACK FOOTER //
// src/components/timesheet/TimesheetsList.vue